import { useState, useEffect, useRef } from 'react';

interface LightboxProps {
  images: string[];
  currentIndex: number;
  onClose: () => void;
  onIndexChange: (index: number) => void;
}

export default function Lightbox({ images, currentIndex, onClose, onIndexChange }: LightboxProps) {
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const imageRef = useRef<HTMLDivElement>(null);

  const zoomLevels = [1, 2, 3];

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') handlePrevious();
      if (e.key === 'ArrowRight') handleNext();
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';
    };
  }, [currentIndex]);

  const handlePrevious = () => {
    if (currentIndex > 0) {
      onIndexChange(currentIndex - 1);
      resetZoom();
    }
  };

  const handleNext = () => {
    if (currentIndex < images.length - 1) {
      onIndexChange(currentIndex + 1);
      resetZoom();
    }
  };

  const handleZoomIn = () => {
    const currentZoomIndex = zoomLevels.indexOf(zoom);
    if (currentZoomIndex < zoomLevels.length - 1) {
      setZoom(zoomLevels[currentZoomIndex + 1]);
    }
  };

  const handleZoomOut = () => {
    const currentZoomIndex = zoomLevels.indexOf(zoom);
    if (currentZoomIndex > 0) {
      setZoom(zoomLevels[currentZoomIndex - 1]);
    }
  };

  const resetZoom = () => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleImageClick = () => {
    const currentZoomIndex = zoomLevels.indexOf(zoom);
    if (currentZoomIndex < zoomLevels.length - 1) {
      setZoom(zoomLevels[currentZoomIndex + 1]);
    } else {
      resetZoom();
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && zoom > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center">
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-50 cursor-pointer"
      >
        <i className="ri-close-line text-4xl"></i>
      </button>

      {/* Zoom Controls */}
      <div className="absolute top-4 left-4 flex flex-col gap-2 z-50">
        <button
          onClick={handleZoomIn}
          disabled={zoom === 3}
          className="bg-white/10 hover:bg-white/20 text-white p-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
        >
          <i className="ri-zoom-in-line text-xl"></i>
        </button>
        <button
          onClick={handleZoomOut}
          disabled={zoom === 1}
          className="bg-white/10 hover:bg-white/20 text-white p-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
        >
          <i className="ri-zoom-out-line text-xl"></i>
        </button>
        <button
          onClick={resetZoom}
          className="bg-white/10 hover:bg-white/20 text-white p-3 rounded-lg transition-colors cursor-pointer"
        >
          <i className="ri-refresh-line text-xl"></i>
        </button>
        <div className="bg-white/10 text-white px-3 py-2 rounded-lg text-center text-sm font-semibold">
          {Math.round(zoom * 100)}%
        </div>
      </div>

      {/* Navigation Arrows */}
      {currentIndex > 0 && (
        <button
          onClick={handlePrevious}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white p-4 rounded-lg transition-colors z-50 cursor-pointer"
        >
          <i className="ri-arrow-left-line text-2xl"></i>
        </button>
      )}

      {currentIndex < images.length - 1 && (
        <button
          onClick={handleNext}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white p-4 rounded-lg transition-colors z-50 cursor-pointer"
        >
          <i className="ri-arrow-right-line text-2xl"></i>
        </button>
      )}

      {/* Image Counter */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-white/10 text-white px-4 py-2 rounded-lg text-sm font-semibold">
        {currentIndex + 1} / {images.length}
      </div>

      {/* Main Image */}
      <div
        ref={imageRef}
        className="relative max-w-7xl max-h-[90vh] overflow-hidden"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <img
          src={images[currentIndex]}
          alt={`Imagem ${currentIndex + 1}`}
          className={`max-w-full max-h-[90vh] object-contain transition-transform duration-300 ${
            zoom > 1 ? 'cursor-move' : 'cursor-zoom-in'
          }`}
          style={{
            transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
            userSelect: 'none'
          }}
          onClick={handleImageClick}
          draggable={false}
        />
      </div>
    </div>
  );
}